import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { missionService, matrixService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { mostraDataHora } from '../utils/dates';

function Submissions() {
  const { missionId } = useParams();
  const { t } = useTranslation();
  const [mission, setMission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [assignats, setAssignats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isInstructor } = useAuth();

  useEffect(() => {
    if (!isInstructor) {
      toast.error(t('submissions.accessDeniedInstructorRequired'));
      return;
    }
    fetchMissionAndSubmissions();
  }, [missionId, isInstructor]);

  const fetchMissionAndSubmissions = async () => {
    try {
      // Fetch mission details
      const missionResponse = await missionService.getMission(missionId);
      setMission(missionResponse.data);

      // Fetch all matrices/submissions for this mission
      const submissionsResponse = await matrixService.getMissionMatrices(missionId);
      setSubmissions(submissionsResponse.data);

      // Els assignats, per poder ensenyar qui NO ha començat. Fins al 05/08/2026
      // aquesta pantalla només llistava les matrius que existien: qui tenia la
      // missió assignada i no hi havia entrat mai no sortia enlloc, que és
      // justament la pregunta que es fa l'instructor als cinc minuts d'obrir la
      // sessió.
      const assignacions = await missionService.getAssignments(missionId);
      setAssignats(assignacions.data?.assigned_users || []);
    } catch (error) {
      toast.error(t('submissions.errorLoadingSubmissions'));
      console.error('Submissions error:', error);
    } finally {
      setLoading(false);
    }
  };

  // El missatge parlava de «Reobrir» sempre, també en una entrega lliurada, on
  // aquell botó no existeix: assenyalava una sortida que no hi era.
  const handleResetSubmission = async (matrixId, username, esCongelada = false) => {
    const missatge = t('submissions.confirmResetSubmission', { username })
      + (esCongelada ? ` ${t('submissions.confirmResetFrozenHint')}` : '');
    if (!window.confirm(missatge)) {
      return;
    }

    try {
      await matrixService.resetMatrix(matrixId);
      toast.success(t('submissions.submissionResetSuccess', { username }));
      fetchMissionAndSubmissions(); // Refresh data
    } catch (error) {
      toast.error(t('submissions.errorResetSubmission'));
    }
  };

  const handleReopenSubmission = async (matrixId, username) => {
    if (!window.confirm(
      t('submissions.confirmReopen', { username }) ||
      `Vols reobrir l'exercici de ${username}? Continuarà on era, amb el temps que li quedava.`
    )) {
      return;
    }

    try {
      await matrixService.reopenMatrix(matrixId);
      toast.success(
        t('submissions.reopenSuccess', { username }) || `Exercici de ${username} reobert`
      );
      fetchMissionAndSubmissions();
    } catch (error) {
      toast.error(t('submissions.errorReopen') || 'No s\'ha pogut reobrir l\'exercici');
    }
  };

  // Temps extra. Es demana amb un prompt a posta: el dia de l'examen, això es fa
  // amb l'alumne al costat i amb pressa, i un formulari modal seria un pas més.
  const demanaMinuts = (missatge) => {
    const resposta = window.prompt(missatge, '10');
    if (resposta === null) return null;
    const minuts = parseInt(resposta, 10);
    if (!Number.isFinite(minuts) || minuts <= 0 || minuts > 180) {
      toast.error(t('submissions.invalidMinutes'));
      return null;
    }
    return minuts;
  };

  const errorDelServidor = (error, perDefecte) =>
    (typeof error.response?.data?.detail === 'string' && error.response.data.detail) || perDefecte;

  const handleExtendSubmission = async (matrixId, username) => {
    const minuts = demanaMinuts(t('submissions.extendPrompt', { username }));
    if (minuts === null) return;
    try {
      await matrixService.extendMatrix(matrixId, minuts);
      toast.success(t('submissions.extendSuccess', { username, minutes: minuts }));
      fetchMissionAndSubmissions();
    } catch (error) {
      toast.error(errorDelServidor(error, t('submissions.errorExtend')));
    }
  };

  const handleExtendMission = async () => {
    const minuts = demanaMinuts(t('submissions.extendMissionPrompt'));
    if (minuts === null) return;
    try {
      const { data } = await missionService.extendMission(missionId, minuts);
      toast.success(t('submissions.extendMissionSuccess', {
        minutes: minuts,
        count: data.matrices_updated,
      }));
      fetchMissionAndSubmissions();
    } catch (error) {
      toast.error(errorDelServidor(error, t('submissions.errorExtend')));
    }
  };

  // Un equip s'ha de reconèixer per «Equip 07», no per «equip-07»: el nom
  // d'usuari és la identitat tècnica —i el subjecte del token—, no el que ha de
  // llegir qui reparteix temps extra enmig d'un examen.
  const nomVisible = (alumne) => alumne?.display_name || alumne?.username || t('submissions.unknownUser');

  // Exportació de resultats.
  //
  // Es genera al navegador i no al servidor per dos motius: aquí les dades ja
  // hi són totes (inclosos els que no han començat) i les hores ja estan en
  // hora d'aquí, mentre que el servidor treballa en UTC i no té la zona
  // horària instal·lada.
  //
  // Separador `;` i marca BOM: és el que fa que el full s'obri bé de doble clic
  // a l'Excel en català, que és on anirà a parar això.
  const exportaCSV = () => {
    const escapa = (valor) => {
      if (valor === null || valor === undefined) return '';
      const text = String(valor);
      return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };

    const capcaleres = [
      t('submissions.student'), t('submissions.email'), t('users.status'),
      t('submissions.started'), t('submissions.submitted'), t('submissions.grade'),
      t('submissions.gradedBy'), t('submissions.gradedOn'),
      t('submissionReview.feedback'), t('submissionReview.studentAssessment'),
    ];

    const estatText = (entrega) => {
      if (noHaComencat(entrega)) return t('submissions.notStarted');
      if (isFrozen(entrega)) return t('submissions.frozen');
      if (entrega.status === 'submitted') return t('submissions.submitted');
      if (entrega.status === 'time_expired') return t('submissions.timeExpired');
      return t('submissions.inProgress');
    };

    const linies = files.map(({ alumne, entrega }) => [
      nomVisible(alumne), alumne?.email, estatText(entrega),
      entrega?.actual_start_time ? mostraDataHora(entrega.actual_start_time) : '',
      entrega?.submitted_at ? mostraDataHora(entrega.submitted_at) : '',
      entrega?.grade ?? '',
      entrega?.graded_by?.username || '',
      entrega?.graded_at ? mostraDataHora(entrega.graded_at) : '',
      entrega?.feedback || '',
      entrega?.conclusion || '',
    ].map(escapa).join(';'));

    const contingut = '\ufeff' + [capcaleres.map(escapa).join(';'), ...linies].join('\r\n');
    const enllac = document.createElement('a');
    enllac.href = URL.createObjectURL(new Blob([contingut], { type: 'text/csv;charset=utf-8;' }));
    // El nom porta la data perquè dos exports del mateix examen no es confonguin.
    const avui = new Date().toISOString().slice(0, 10);
    enllac.download = `ach-${(mission?.title || 'missio')}-${avui}.csv`.replace(/[^\w.\-]+/g, '-');
    document.body.appendChild(enllac);
    enllac.click();
    document.body.removeChild(enllac);
    URL.revokeObjectURL(enllac.href);
    toast.success(t('submissions.exportDone', { count: files.length }));
  };

  const formatRemaining = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  };

  // Una matriu congelada segueix sent «in_progress» a la base de dades: el que
  // la distingeix és frozen_at. Per a l'instructor, però, són dues situacions
  // ben diferents —una és algú treballant i l'altra és algú que espera que
  // l'ajudin— i el llistat les ha de separar.
  const isFrozen = (submission) =>
    submission?.status === 'in_progress' && Boolean(submission.frozen_at);

  // Qui no ha arrencat el rellotge no ha començat, tant si no té matriu com si
  // en té una de creada per haver obert la pàgina i prou. Fins ara aquest segon
  // cas sortia com a «en curs», i eren exercicis que no havia tocat ningú.
  const noHaComencat = (submission) => !submission || !submission.actual_start_time;

  const getStatusBadge = (submission) => {
    if (noHaComencat(submission)) {
      return <span className="badge badge-secondary">{t('submissions.notStarted')}</span>;
    }
    if (isFrozen(submission)) {
      const restant = formatRemaining(submission.time_remaining_seconds);
      return (
        <span className="badge badge-warning" title={t('submissions.frozenHint') || 'Cal reobrir-lo'}>
          {t('submissions.frozen') || 'Congelat'}
          {restant ? ` (${restant})` : ''}
        </span>
      );
    }
    const statusMap = {
      'in_progress': { class: 'badge-warning', text: t('submissions.inProgress') },
      'submitted': { class: 'badge-success', text: t('submissions.submitted') },
      'time_expired': { class: 'badge-danger', text: t('submissions.timeExpired') }
    };
    const statusInfo = statusMap[submission.status]
      || { class: 'badge-secondary', text: submission.status };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  // El servidor envia les hores en UTC i sense marca de fus. `new Date()` les
  // llegia com a hora local, o sigui que tota aquesta pantalla ensenyava les
  // hores dues hores endarrerides a l'estiu.
  const formatDateTime = (dateString) => mostraDataHora(dateString, t('common.notAvailable'));

  if (!isInstructor) {
    return (
      <div className="card">
        <h3>{t('submissions.accessDenied')}</h3>
        <p>{t('submissions.instructorOnlyAccess')}</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">{t('submissions.loadingSubmissions')}</div>;
  }

  if (!mission) {
    return (
      <div className="card">
        <h3>{t('submissions.missionNotFound')}</h3>
        <p>{t('submissions.missionNotFoundDescription')}</p>
      </div>
    );
  }

  // Una fila per alumne assignat, tingui matriu o no. S'hi afegeixen les
  // matrius d'algú que ja no estigui assignat: existeixen i s'han de veure.
  const perUsuari = new Map(submissions.map(s => [s.user_id, s]));
  const desassignats = submissions.filter(s => !assignats.some(a => a.id === s.user_id));
  const files = [
    ...assignats.map(a => ({ alumne: a, entrega: perUsuari.get(a.id) || null })),
    ...desassignats.map(s => ({ alumne: s.user || { username: t('submissions.unknownUser') }, entrega: s })),
  ].sort((x, y) => nomVisible(x.alumne).localeCompare(nomVisible(y.alumne)));

  const submittedCount = submissions.filter(s => s.status === 'submitted' || s.status === 'time_expired').length;
  const inProgressCount = files.filter(f => f.entrega && !noHaComencat(f.entrega) && f.entrega.status === 'in_progress').length;
  const notStartedCount = files.filter(f => noHaComencat(f.entrega)).length;
  const gradedCount = submissions.filter(s => s.graded_at).length;

  return (
    <div>
      <div className="submissions-header">
        <div className="header-content">
          <div className="breadcrumb">
            <Link to="/missions" className="breadcrumb-link">{t('nav.missions')}</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={`/missions/${mission.id}`} className="breadcrumb-link">{mission.title}</Link>
            <span className="breadcrumb-separator">/</span>
            <span>{t('submissions.submissions')}</span>
          </div>
          <h1>{t('submissions.submissionsTitle')}: {mission.title}</h1>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            <button
              onClick={handleExtendMission}
              className="btn btn-secondary btn-sm"
              title={t('submissions.extendMissionHint')}
            >
              {t('submissions.extendMission')}
            </button>
            <button
              onClick={exportaCSV}
              className="btn btn-secondary btn-sm"
              title={t('submissions.exportHint')}
            >
              {t('submissions.export')}
            </button>
          </div>
        </div>

        <div className="submission-stats">
          <div className="stat-card">
            <div className="stat-number">{submittedCount}</div>
            <div className="stat-label">{t('submissions.submitted')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{inProgressCount}</div>
            <div className="stat-label">{t('submissions.inProgress')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{notStartedCount}</div>
            <div className="stat-label">{t('submissions.notStarted')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{gradedCount}</div>
            <div className="stat-label">{t('submissions.graded')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{files.length}</div>
            <div className="stat-label">{t('submissions.assignedStudents')}</div>
          </div>
        </div>
      </div>

      <div className="mission-info-card">
        <div className="mission-details">
          <p><strong>{t('missions.timeLimitMinutes')}:</strong> {mission.time_limit_minutes} {t('submissions.minutes')}</p>
          <p><strong>{t('users.status')}:</strong> <span className={`badge badge-${mission.is_active ? 'success' : 'warning'}`}>
            {mission.is_active ? t('common.active') : t('common.inactive')}
          </span></p>
          {/* La finestra de la missió. Des del 05/08/2026 vol dir alguna cosa:
              fora d'aquestes hores no es pot començar, i el tancament tanca
              l'exercici encara que a l'alumne li quedessin minuts. */}
          {(mission.start_time || mission.end_time) ? (
            <p>
              <strong>{t('submissions.missionWindow')}:</strong>{' '}
              {mission.start_time ? formatDateTime(mission.start_time) : t('submissions.windowNoStart')}
              {' → '}
              {mission.end_time ? formatDateTime(mission.end_time) : t('submissions.windowNoEnd')}
            </p>
          ) : (
            <p><strong>{t('submissions.missionWindow')}:</strong> {t('submissions.windowAlwaysOpen')}</p>
          )}
        </div>
      </div>

      {files.length === 0 ? (
        <div className="card">
          <h3>{t('submissions.noSubmissionsYet')}</h3>
          <p>{t('submissions.noStudentsStarted')}</p>
          <p>{t('submissions.studentsCanAccess')} <Link to="/missions">{t('nav.missions')}</Link> {t('submissions.page')}.</p>
        </div>
      ) : (
        <div className="submissions-list">
          <div className="submissions-table">
            <div className="table-header">
              <div className="table-row">
                <div className="table-cell header-cell">{t('submissions.student')}</div>
                <div className="table-cell header-cell">{t('users.status')}</div>
                <div className="table-cell header-cell">{t('submissions.started')}</div>
                <div className="table-cell header-cell">{t('submissions.submitted')}</div>
                <div className="table-cell header-cell">{t('submissions.lastActivity')}</div>
                <div className="table-cell header-cell">{t('submissions.grade')}</div>
                <div className="table-cell header-cell">{t('users.actions')}</div>
              </div>
            </div>
            <div className="table-body">
              {files.map(({ alumne, entrega }) => (
                <div key={alumne?.id || `m${entrega?.id}`} className="table-row submission-row">
                  <div className="table-cell">
                    <div className="student-info">
                      <span className="student-name">{nomVisible(alumne)}</span>
                    </div>
                  </div>
                  <div className="table-cell">
                    {getStatusBadge(entrega)}
                  </div>
                  <div className="table-cell">
                    {entrega?.actual_start_time ? formatDateTime(entrega.actual_start_time) : '-'}
                  </div>
                  <div className="table-cell">
                    {entrega && (entrega.status === 'submitted' || entrega.status === 'time_expired')
                      ? formatDateTime(entrega.submitted_at)
                      : '-'
                    }
                  </div>
                  <div className="table-cell">
                    {entrega ? formatDateTime(entrega.last_saved_at) : '-'}
                  </div>
                  {/* Qui queda per corregir es veu d'un cop d'ull, sense haver
                      d'obrir les entregues una per una. */}
                  <div className="table-cell">
                    {entrega?.graded_at ? (
                      <span className="badge badge-success">
                        {entrega.grade !== null && entrega.grade !== undefined
                          ? `${entrega.grade} / 10`
                          : t('submissions.gradedNoMark')}
                      </span>
                    ) : (
                      entrega && (entrega.status === 'submitted' || entrega.status === 'time_expired') && (
                        <span className="badge badge-secondary">{t('submissions.notGraded')}</span>
                      )
                    )}
                  </div>
                  <div className="table-cell">
                    <div className="action-buttons">
                      {entrega && (entrega.status === 'submitted' || entrega.status === 'time_expired') && (
                        <Link
                          to={`/review/${entrega.id}`}
                          className="btn btn-primary btn-sm"
                          title={t('submissions.reviewSubmission')}
                        >
                          {t('submissions.review')}
                        </Link>
                      )}
                      {entrega && entrega.status === 'in_progress' && !noHaComencat(entrega) && (
                        <Link
                          to={`/review/${entrega.id}`}
                          className="btn btn-secondary btn-sm"
                          title={t('submissions.viewProgress')}
                        >
                          {t('submissions.viewProgress')}
                        </Link>
                      )}
                      {isFrozen(entrega) && (
                        <button
                          onClick={() => handleReopenSubmission(entrega.id, nomVisible(alumne))}
                          className="btn btn-primary btn-sm"
                          title={t('submissions.reopenSubmission')
                            || 'Continua on era, amb el temps que li quedava'}
                        >
                          {t('submissions.reopen') || 'Reobrir'}
                        </button>
                      )}
                      {/* Només té sentit donar temps a qui ha arrencat el
                          rellotge: qui no ha premut «començar» encara té el
                          temps sencer de la missió per endavant. */}
                      {entrega && entrega.status !== 'submitted' && entrega.actual_start_time && (
                        <button
                          onClick={() => handleExtendSubmission(entrega.id, nomVisible(alumne))}
                          className="btn btn-secondary btn-sm"
                          title={t('submissions.extendSubmissionHint')}
                        >
                          {t('submissions.extend')}
                        </button>
                      )}
                      {/* A qui no ha començat no hi ha res a reiniciar. */}
                      {entrega && !noHaComencat(entrega) && (
                        <button
                          onClick={() => handleResetSubmission(entrega.id, nomVisible(alumne), isFrozen(entrega))}
                          className="btn btn-warning btn-sm"
                          title={t('submissions.resetSubmission')}
                        >
                          {t('submissions.reset')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Submissions;